/**
 * discount-animation.js
 * ——————————————————————
 * Confetti + bouncing card animation.
 * Themes: discount20 (direct) · influencer (Collabs / KOL)
 *
 * Mapping format from Liquid:
 *   CODE:theme:display_name:discount_percent
 *
 * The influencer builder dynamically inserts the creator's name
 * and their specific discount percentage into the card.
 */

(function () {
  'use strict';

  var DURATION_MS = 8000;
  var SESSION_KEY = 'da_played';

  var CONFETTI_COLORS_DEFAULT = [
    '#FF6B6B', '#FECA57', '#48DBFB', '#FF9FF3',
    '#1DD1A1', '#FF6348', '#A29BFE', '#FD79A8',
    '#00D2D3', '#FDCB6E', '#6C5CE7', '#E17055',
  ];
  var CONFETTI_COLORS_INFLUENCER = [
    '#A29BFE', '#FF9FF3', '#DCD6FF', '#FD79A8',
    '#C4B5FD', '#F0ABFC', '#818CF8', '#E879F9',
    '#A78BFA', '#F9A8D4', '#7C3AED', '#DB2777',
  ];
  var CONFETTI_DESKTOP = 55;
  var CONFETTI_MOBILE  = 25;

  /* ── Helpers ─────────────────────────────────────────────── */

  function getCodeFromURL() {
    var m = window.location.pathname.match(/\/discount\/([^/?#]+)/i);
    return m ? m[1].toUpperCase() : null;
  }

  function getCodeFromCookie() {
    var m = document.cookie.match(/(?:^|;\s*)discount_code=([^;]+)/);
    return m ? decodeURIComponent(m[1]).toUpperCase() : null;
  }

  /**
   * Parse the Liquid-generated mapping string.
   *
   * Format: CODE:theme:name:discount  (pipe-separated entries)
   *
   * Returns: { 'CODE': { theme: 'xxx', name: 'Xxx', discount: 'NN' }, ... }
   */
  function parseMap(str) {
    var map = {};
    (str || '').split('|').forEach(function (entry) {
      var p = entry.split(':');
      if (p.length >= 2) {
        var code     = p[0].trim().toUpperCase();
        var theme    = p[1].trim().toLowerCase();
        var name     = (p[2] || '_').trim();
        var discount = (p[3] || '20').trim();

        // _ means no name (used for direct discount theme)
        if (name === '_') name = '';

        map[code] = {
          theme: theme,
          name: name,
          discount: discount,
        };
      }
    });
    return map;
  }

  /** Escape HTML special chars to prevent XSS from Liquid data */
  function escHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  function rand(min, max) { return min + Math.random() * (max - min); }

  function isMobile() {
    return window.innerWidth <= 480 ||
      ('ontouchstart' in window && window.innerWidth <= 768);
  }

  function prefersReducedMotion() {
    return window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ── DOM Builders (shared) ─────────────────────────────── */

  function buildConfetti(colors) {
    var count = isMobile() ? CONFETTI_MOBILE : CONFETTI_DESKTOP;
    if (prefersReducedMotion()) return '';
    var mobile = isMobile();
    var html = '';
    var shapes = ['', 'da-confetti-circle'];
    for (var i = 0; i < count; i++) {
      var x = rand(0, 100).toFixed(1);
      var delay = rand(0, 1.6).toFixed(2);
      var dur = rand(2.2, 4).toFixed(2);
      var color = colors[i % colors.length];
      var drift = (rand(mobile ? -40 : -70, mobile ? 40 : 70)).toFixed(0);
      var rot = (rand(0, 360)).toFixed(0);
      var size = rand(mobile ? 5 : 6, mobile ? 10 : 14).toFixed(0);
      var isRect = Math.random() > 0.6;
      var h = isRect ? (size * 0.45).toFixed(0) : size;
      var shape = shapes[Math.floor(Math.random() * shapes.length)];

      html += '<div class="da-confetti" style="' +
        'left:' + x + '%;' +
        'animation-delay:' + delay + 's;' +
        'animation-duration:' + dur + 's;' +
        '--da-drift:' + drift + 'px;' +
        '--da-rot:' + rot + 'deg;">' +
        '<div class="da-confetti-shape ' + shape + '" style="' +
        'background:' + color + ';' +
        'width:' + size + 'px;' +
        'height:' + h + 'px;"></div></div>';
    }
    return html;
  }

  function buildSparkles() {
    if (isMobile() || prefersReducedMotion()) return '';
    var html = '';
    for (var i = 0; i < 8; i++) {
      var angle = i * 45;
      html += '<span class="da-sparkle-dot" style="' +
        'transform:rotate(' + angle + 'deg) translateY(-56px);' +
        'animation-delay:' + (0.65 + i * 0.07).toFixed(2) + 's;"></span>';
    }
    return html;
  }

  /* ── Theme Builders ────────────────────────────────────── */

  var builders = {

    /**
     * Direct discount — gold accent
     * data.discount = percentage number (e.g. "20")
     */
    discount20: function (overlay, data) {
      var pct = escHtml(data.discount);
      overlay.innerHTML =
        '<div class="da-confetti-layer">' + buildConfetti(CONFETTI_COLORS_DEFAULT) + '</div>' +
        '<div class="da-card">' +
          '<div class="da-sparkle-ring">' + buildSparkles() + '</div>' +
          '<div class="da-icon">🎉</div>' +
          '<div class="da-eyebrow">DISCOUNT' + pct + '</div>' +
          '<h1 class="da-title">Congrats!<br>' +
            '<span class="da-title-sub">You found a ' +
              '<strong class="da-highlight">' + pct + '% off</strong> Coupon!</span>' +
          '</h1>' +
          '<div class="da-divider"></div>' +
          '<p class="da-footer">Automatically applied at checkout</p>' +
          '<button class="da-btn da-start-shopping" aria-label="Start shopping">Start Shopping →</button>' +
        '</div>';
    },

    /**
     * Influencer / Collabs — purple accent
     * data.name     = creator display name (e.g. "Sarah")
     * data.discount = percentage number (e.g. "20")
     */
    influencer: function (overlay, data) {
      var name = escHtml(data.name);
      var pct  = escHtml(data.discount);
      overlay.innerHTML =
        '<div class="da-confetti-layer">' + buildConfetti(CONFETTI_COLORS_INFLUENCER) + '</div>' +
        '<div class="da-card">' +
          '<div class="da-sparkle-ring">' + buildSparkles() + '</div>' +
          '<div class="da-icon">✨</div>' +
          '<div class="da-eyebrow">' + name.toUpperCase() + '\'S PICK</div>' +
          '<h1 class="da-title">You\'re In!<br>' +
            '<span class="da-title-sub">' + name + ' scored you ' +
              '<strong class="da-highlight">' + pct + '% off</strong></span>' +
          '</h1>' +
          '<div class="da-divider"></div>' +
          '<p class="da-footer">Recommended by ' + name + ' · applied at checkout</p>' +
          '<button class="da-btn da-start-shopping" aria-label="Start shopping">Start Shopping →</button>' +
        '</div>';
    },
  };

  /* ── Main ────────────────────────────────────────────────── */

  function init() {
    if (sessionStorage.getItem(SESSION_KEY)) return;

    var cfg = document.getElementById('da-config');
    var overlay = document.getElementById('da-overlay');
    if (!cfg || !overlay) return;

    var code = getCodeFromURL() || getCodeFromCookie();
    if (!code) return;

    var animMap = parseMap(cfg.dataset.animationMap);
    var data = animMap[code];
    if (!data || !builders[data.theme]) return;

    sessionStorage.setItem(SESSION_KEY, '1');

    overlay.classList.add('da-theme-' + data.theme);
    builders[data.theme](overlay, data);

    requestAnimationFrame(function () {
      overlay.classList.add('da-active');
      overlay.setAttribute('aria-hidden', 'false');
    });

    var shopBtn = overlay.querySelector('.da-start-shopping');
    if (shopBtn) shopBtn.addEventListener('click', dismiss);
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) dismiss();
    });

    var autoClose = setTimeout(dismiss, DURATION_MS);

    function dismiss() {
      clearTimeout(autoClose);
      overlay.classList.remove('da-active');
      overlay.setAttribute('aria-hidden', 'true');
      setTimeout(function () {
        overlay.innerHTML = '';
        overlay.className = 'da-overlay';
      }, 480);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();